import { QueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { DateFormat } from 'constants/General'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'
import { DEFAULT_NUMBER_OF_ROWS } from './RewardsDetails'
import { RewardsDataPoint } from './types'

dayjs.extend(advancedFormat)

export const formatEndOfMonth = () => {
  return dayjs().endOf('month').format(DateFormat.MMM_D)
}

export function calculateCompoundRewards(initialAmount: number, apy: number) {
  let currentPrincipal = initialAmount
  let monthlyRewards = 0
  const decimalAnnualRate = apy / 100

  // Calculate true monthly compound rate from APY
  const monthlyRate = Math.pow(1 + decimalAnnualRate, 1 / 12) - 1

  // Use dayjs to calculate actual date ranges
  const startDate = dayjs()
  const endDate = startDate.add(3, 'years')
  const totalDays = endDate.diff(startDate, 'days')

  return Array.from({ length: totalDays }, (_, dayIndex) => {
    const currentDate = startDate.add(dayIndex, 'days')
    const isEndOfMonth = currentDate.date() === currentDate.daysInMonth()

    // Calculate daily reward based on current principal and monthly rate
    if (dayIndex > 0) {
      // Get actual days in current month for accurate daily distribution
      const actualDaysInMonth = currentDate.daysInMonth()
      const monthlyInterest = currentPrincipal * monthlyRate
      const dailyReward = monthlyInterest / actualDaysInMonth
      monthlyRewards += dailyReward
    }

    // At the end of each calendar month, compound the rewards into principal
    if (isEndOfMonth && dayIndex > 0) {
      currentPrincipal += monthlyRewards
      monthlyRewards = 0
    }

    const tooltipLabel = generateTooltipLabelBasedOnDays(dayIndex)
    const totalValue = Math.round(currentPrincipal + monthlyRewards)

    return {
      day: dayIndex.toString(),
      rewards: totalValue,
      tooltipLabel,
    }
  })
}

export function calculateCompoundedRewardsForSingleDay(initialAmount: number, apy: number) {
  const decimalAnnualRate = apy / 100
  const DAYS_IN_YEAR = 365
  const dailyReward = initialAmount * (decimalAnnualRate / DAYS_IN_YEAR)

  return Math.round(dailyReward * 100) / 100
}

export function generateTooltipLabelBasedOnDays(day: number): string {
  if (day === 0) {
    return 'Today'
  }

  if (day === 365) {
    return '1 year'
  }

  if (day === 730) {
    return '2 years'
  }

  if (day === 1095) {
    return '3 years'
  }

  if (day < 30) {
    return `${day} ${day === 1 ? 'day' : 'days'}`
  }

  if (day < 365) {
    const months = Math.floor(day / 30)
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }

  const years = Math.floor(day / 365)
  const remainingDays = day % 365
  const months = Math.floor(remainingDays / 30)

  if (months === 0) {
    return `${years} ${years === 1 ? 'year' : 'years'}`
  }

  return `${years} ${years === 1 ? 'year' : 'years'} and ${months} ${months === 1 ? 'month' : 'months'}`
}

const PADDING_FACTOR = 0.05
export function calculateChartTicks(data: RewardsDataPoint[]) {
  const maxValue = Math.max(...data.map(d => d.rewards))
  const minValue = Math.min(...data.map(d => d.rewards))
  const dataRange = maxValue - minValue

  // Use minimal padding on both sides for balanced chart appearance
  const topPadding = dataRange * PADDING_FACTOR // 5% padding on top
  const bottomPadding = dataRange * PADDING_FACTOR // 5% padding on bottom
  const paddedMin = minValue - bottomPadding
  const paddedMax = maxValue + topPadding

  // Determine formatting scale
  let scale = 1
  if (paddedMax >= 1000000) {
    scale = 1000000
  } else if (paddedMax >= 1000) {
    scale = 1000
  }

  // Convert to display units
  const displayMin = paddedMin / scale
  const displayMax = paddedMax / scale
  const displayRange = displayMax - displayMin

  // Calculate nice step size (prefer whole numbers like 1, 2, 5, 10, 20, 50, 100, etc)
  const minTickCount = 6
  const roughStep = displayRange / (minTickCount - 1)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const normalizedStep = roughStep / magnitude
  let niceStep = 10
  if (normalizedStep <= 1) {
    niceStep = 1
  } else if (normalizedStep <= 2) {
    niceStep = 2
  } else if (normalizedStep <= 5) {
    niceStep = 5
  }
  const displayStep = niceStep * magnitude

  // Round min/max to nice boundaries
  const roundedMin = Math.floor(displayMin / displayStep) * displayStep
  const roundedMax = Math.ceil(displayMax / displayStep) * displayStep

  // Generate evenly-spaced ticks (ensure at least minTickCount)
  const tickCount = Math.max(minTickCount, Math.round((roundedMax - roundedMin) / displayStep) + 1)
  const axisTicks = Array.from({ length: tickCount }, (_, i) => {
    const tickValue = (roundedMin + i * displayStep) * scale
    // Round to avoid floating-point precision issues (e.g., 1150.0000000000002 -> 1150)
    return Math.round(tickValue * 100) / 100
  })

  return {
    tickCount,
    axisTicks,
  }
}

export const formatAPY = (rate: number) => `${formatAmount(rate)}%`

export const formatAmount = (amount: number) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const isValidAmount = (value: string) => {
  if (!value || typeof value !== 'string') return false

  const cleanedValue = value.replaceAll(',', '').trim()
  const amountPattern = /^(?!0$)\d+(\.\d+)?$/

  if (!amountPattern.test(cleanedValue)) return false

  const numValue = Number.parseFloat(cleanedValue)
  return numValue > 0
}

export const invalidateRewardsQueries = (
  reactQueryClient: QueryClient,
  walletReference?: string
) => {
  reactQueryClient.invalidateQueries({
    queryKey: reactQueryKeys.rewardsPortfolios(API_PAGINATION_OFFSET, API_PAGINATION_LIMIT),
  })
  reactQueryClient.invalidateQueries({
    queryKey: reactQueryKeys.ledgerWalletTransactions({
      size: API_PAGINATION_LIMIT,
      page: API_PAGINATION_OFFSET,
      categories: ['EARNING'],
    }),
  })

  reactQueryClient.invalidateQueries({
    queryKey: reactQueryKeys.ledgerWalletTransactions({
      walletReference,
      size: DEFAULT_NUMBER_OF_ROWS,
      page: API_PAGINATION_OFFSET,
    }),
  })
}
